import type { Directive } from 'vue'

/**
 * `v-no-autofill` — suppress browser / password-manager autofill on a text or
 * password input, including PrimeVue-wrapped ones (`Password`, `InputText`).
 *
 * Chrome ignores `autocomplete="off"` on fields it heuristically matches, but
 * it keys autofill off the field's `name` / `id`. Stamping a RANDOM `name` (so
 * it can't be matched to saved data) is what reliably stops the fill — verified
 * in-browser. `autocomplete="off"` is kept as a harmless extra signal.
 *
 * Deliberately does NOT use the readonly-until-focus trick — the random name
 * alone is sufficient and keeps the field immediately editable.
 *
 * Usage: `<InputText v-no-autofill v-model="q" />` /
 * `<Password v-no-autofill v-model="pin" />`. The directive locates the inner
 * `<input>` itself, so it works whether the bound element IS the input or wraps
 * it.
 */
export const vNoAutofill: Directive<HTMLElement> = {
  mounted(el) {
    const input: HTMLInputElement | null =
      el instanceof HTMLInputElement ? el : el.querySelector('input')
    if (input === null) {
      return
    }
    input.setAttribute('name', `nf_${Math.random().toString(36).slice(2, 10)}`)
    input.setAttribute('autocomplete', 'off')
  },
}

export default vNoAutofill
